/** @file flattenPlan: which source nodes merge onto host nodes and which enter the host under a prefix. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';

/**
 * Source nodes named in `nodeMap` merge onto the host node they map to (`NOT_FOUND` when either is missing); every
 * other source node is added as `prefix + name` (`VALIDATION` when that name is taken or a source root is unmapped).
 * `rename` maps every source node name to its host name.
 */
export function flattenPlan(host: Puppet, source: Puppet, nodeMap: Readonly<Record<string, string>>, prefix: string): { merged: string[]; added: string[]; rename: Map<string, string> } {
  for (const [from, to] of Object.entries(nodeMap)) {
    const path = `/input/nodeMap/${from}`;
    if (!Object.hasOwn(source.nodes, from)) throwError(ERROR_CODES.NOT_FOUND, { node: from }, { path, hint: 'a node of the source puppet' });
    if (!Object.hasOwn(host.nodes, to)) throwError(ERROR_CODES.NOT_FOUND, { node: to }, { path, hint: 'a node of the host puppet' });
  }
  const rename = new Map<string, string>();
  const [merged, added]: [string[], string[]] = [[], []];
  for (const [name, node] of Object.entries(source.nodes)) {
    const to = nodeMap[name];
    if (to !== undefined) {
      merged.push(name);
      rename.set(name, to);
      continue;
    }
    if (node.parent === undefined) throwError(ERROR_CODES.VALIDATION, { node: name }, { path: '/input/nodeMap', hint: `map the source root ${name} onto a host node` });
    if (Object.hasOwn(host.nodes, prefix + name)) throwError(ERROR_CODES.VALIDATION, { node: prefix + name }, { path: '/input/id', hint: 'the host has a node with the flattened name' });
    added.push(prefix + name);
    rename.set(name, prefix + name);
  }
  return { merged, added, rename };
}
